import React, { useEffect, useState } from 'react';

import { makeStyles } from '@mui/styles';

import {
  CollectionMethod,
  MemberEngagementLevel,
} from '@vestahealthcare/common/enums';
import Enum, {
  Selectable,
  SelectableInfo,
} from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  FilterSelectable,
  MemberWarningType,
  Organization,
  PodConfig,
  ProgramExtension,
  StoredFilter,
} from '@vestahealthcare/common/models';

import {
  Panel,
  Select,
  SelectLanguage,
  SelectProgramExtension,
  SelectProgramExtensionData,
} from 'styleguide-v2';
import { FilterGroup, FilterItem } from 'styleguide-v2/src/components/Panel';

import {
  FiltersVestaTeam,
  VestaTeamKey,
} from 'dash/src/components/filters/FiltersVestaTeam';
import { GetPodReportDataParams } from 'dash/src/services/CCMServices';
import { dataUtils } from 'dash/src/utils/dataUtils';
import { getFilterCount, getFilterItem } from 'dash/src/utils/filterUtils';

import { MemberWarningDropdown } from '../../../components/MemberWarningDropdown';
import { ReferralDropdown } from '../../../components/ReferralDropdown';
import { POD_REPORTING_IS_UNREACHABLE_OPTIONS } from './PodReportingFiltersBar';

type KeyGetPodParams = keyof GetPodReportDataParams;

type Props = {
  data: {
    employees: SelectableInfo<Employee>[];
    languages: Selectable[];
    pods: SelectableInfo<PodConfig>[];
    programExtensions: SelectableInfo<ProgramExtension>[];
    referralSources: SelectableInfo<Organization>[];
    status: Selectable[];
    warningTypes: SelectableInfo<MemberWarningType>[];
    worklistGroups: Selectable[];
  };
  disabledFilters?: KeyGetPodParams[];
  filters?: {
    [x in KeyGetPodParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetPodParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
  onDeleteFilter: (filter: StoredFilter) => Promise<void>;
  onEditFilter: (filter: StoredFilter) => Promise<void>;
  onSaveFilter: (filter: StoredFilter) => Promise<void>;
  selectedPE?: number;
  storedFilters?: StoredFilter[];
};

const MEMBER_FILTERS: KeyGetPodParams[] = [
  'status',
  'language',
  'referralSource',
];

const PROGRAM_FILTERS: KeyGetPodParams[] = [
  'programExtensionFilter',
  'excludedProgramExtensionId',
  'worklistGroupId',
];

const VESTA_TEAM_FILTERS: VestaTeamKey[] = [
  'podId',
  'podManagerId',
  'npOwnerId',
  'rnOwnerId',
  'healthCoachId',
  'careCoordinatorId',
];

const RPM_FILTERS: KeyGetPodParams[] = ['vitalCollectionMethod'];

const OPS_FILTERS: KeyGetPodParams[] = [
  'engagementLevel',
  'isUnreachable',
  'warningTypeId',
];

type FilterType = {
  [x in KeyGetPodParams]?: FilterItem;
};

const useStyles = makeStyles({
  container: {
    '&&': {
      marginTop: 0,
    },
  },
});

export const PodReportingFiltersModal = ({
  data,
  disabledFilters,
  filters,
  loadingFilters,
  open,
  onChange,
  onClose,
  onDeleteFilter,
  onEditFilter,
  onSaveFilter,
  selectedPE,
  storedFilters,
}: Props) => {
  const styles = useStyles();

  const [pendingFilters, setPendingFilters] = useState<FilterType>(
    filters || {},
  );

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  return (
    <Panel.FilterModal
      className={styles.container}
      open={open}
      onClear={() => {
        onChange({
          isUnreachable: getFilterItem(
            translate('podReporting.filters.unreachable'),
            false,
          ),
        });
      }}
      onClose={onClose}
      onDeleteFilter={onDeleteFilter}
      onEditFilter={onEditFilter}
      onSaveFilter={onSaveFilter}
      onSubmit={() => onChange(pendingFilters)}
      size={400}
      storedFilters={storedFilters}
    >
      <FilterGroup
        title={translate('components.FilterGroup.member')}
        count={getFilterCount(pendingFilters, MEMBER_FILTERS)}
      >
        <Select
          data-cy="pod-reporting-modal-status"
          disabled={disabledFilters?.includes('status')}
          items={data.status}
          label={translate('podReporting.filters.memberStatus')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              status: getFilterItem(
                translate('podReporting.filters.memberStatus'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.status?.value || []}
        />
        <SelectLanguage
          data-cy="pod-reporting-modal-language"
          disabled={disabledFilters?.includes('language')}
          items={data.languages}
          label={translate('podReporting.filters.language')}
          limitTags={1}
          loading={loadingFilters}
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              language: getFilterItem(
                translate('podReporting.filters.language'),
                values,
              ),
            });
          }}
          size="small"
          value={(pendingFilters.language?.value as Selectable[]) || []}
        />
        <ReferralDropdown
          data-cy="pod-reporting-modal-referral-sources"
          items={data.referralSources}
          label={translate('podReporting.filters.referralSources')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              referralSource: getFilterItem(
                translate('podReporting.filters.referralSources'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.referralSource?.value || []}
        />
      </FilterGroup>
      <FilterGroup
        title={translate('components.FilterGroup.program')}
        count={getFilterCount(pendingFilters, PROGRAM_FILTERS)}
      >
        <SelectProgramExtension
          items={data.programExtensions?.map(
            ({ info }) => info as ProgramExtension,
          )}
          itemsEnabled={selectedPE ? [selectedPE] : undefined}
          label={translate('podReporting.filters.programExtensions')}
          limitTags={1}
          onChange={(value) =>
            setPendingFilters({
              ...pendingFilters,
              programExtensionFilter: value?.length
                ? getFilterItem(
                    translate('podReporting.filters.programExtensions'),
                    value,
                    dataUtils.programExtensions.getFilterItem,
                  )
                : undefined,
            })
          }
          size="small"
          value={(pendingFilters?.programExtensionFilter
            ?.value as FilterSelectable<SelectProgramExtensionData>[])
            ?.map(({ value }) => value)
            ?.filter(
              (item) =>
                !selectedPE || item?.programExtension?.id === selectedPE,
            )}
        />
        <Select
          data-cy="pod-reporting-modal-pe"
          disabled={disabledFilters?.includes('excludedProgramExtensionId')}
          getItemDisabled={({ value }: Selectable) => value === selectedPE}
          items={data.programExtensions}
          label={translate('podReporting.filters.excludedProgramExtensions')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              excludedProgramExtensionId: getFilterItem(
                translate('podReporting.filters.excludedProgramExtensions'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.excludedProgramExtensionId?.value || []}
        />
        <Select
          data-cy="pod-reporting-modal-care-plan-group"
          disabled={disabledFilters?.includes('worklistGroupId')}
          items={data.worklistGroups}
          label={translate('podReporting.filters.groups')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              worklistGroupId: getFilterItem(
                translate('podReporting.filters.groups'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.worklistGroupId?.value || []}
        />
      </FilterGroup>
      <FiltersVestaTeam
        data={{ employees: data.employees, pods: data.pods }}
        disabledFilters={disabledFilters as VestaTeamKey[]}
        filters={pendingFilters}
        keys={VESTA_TEAM_FILTERS}
        loading={loadingFilters}
        onChange={(updatedFilters) =>
          setPendingFilters({ ...pendingFilters, ...updatedFilters })
        }
      />
      {selectedPE === ProgramExtension.RPM && (
        <FilterGroup
          title={translate('components.FilterGroup.rpm')}
          count={getFilterCount(pendingFilters, RPM_FILTERS)}
        >
          <Select
            data-cy="pod-reporting-modal-vitals-collection-method"
            items={Enum.toSelectable(CollectionMethod.asArray)}
            label={translate('podReporting.filters.vitalsCollectionMethod')}
            limitTags={1}
            multiple
            onChange={(values: Selectable[]) => {
              setPendingFilters({
                ...pendingFilters,
                vitalCollectionMethod: getFilterItem(
                  translate('podReporting.filters.vitalsCollectionMethod'),
                  values,
                ),
              });
            }}
            size="small"
            value={pendingFilters.vitalCollectionMethod?.value || []}
          />
        </FilterGroup>
      )}
      <FilterGroup
        title={translate('components.FilterGroup.ops')}
        count={getFilterCount(pendingFilters, OPS_FILTERS)}
      >
        <Select
          data-cy="pod-reporting-modal-engagement-level"
          items={Enum.toSelectable(MemberEngagementLevel.asArray)}
          label={translate('podReporting.filters.engagementLevel')}
          limitTags={1}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              engagementLevel: getFilterItem(
                translate('podReporting.filters.engagementLevel'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.engagementLevel?.value || []}
        />
        <Select
          data-cy="pod-reporting-modal-unreachable"
          disabled={disabledFilters?.includes('isUnreachable')}
          disableClearable
          items={POD_REPORTING_IS_UNREACHABLE_OPTIONS}
          label={translate('podReporting.filters.unreachable')}
          onChange={(value?: Selectable) => {
            setPendingFilters({
              ...pendingFilters,
              isUnreachable: getFilterItem(
                translate('podReporting.filters.unreachable'),
                value,
              ),
            });
          }}
          size="small"
          value={
            pendingFilters.isUnreachable?.value as FilterSelectable<
              boolean | undefined
            >
          }
        />
        <MemberWarningDropdown
          items={data.warningTypes}
          onChange={(value: SelectableInfo<MemberWarningType>) => {
            setPendingFilters({
              ...pendingFilters,
              warningTypeId: getFilterItem(
                translate('components.MemberWarningDropdown.label'),
                value,
              ),
            });
          }}
          size="small"
          value={pendingFilters.warningTypeId?.value}
        />
      </FilterGroup>
    </Panel.FilterModal>
  );
};
